import Swiper from 'swiper/bundle';

// =============================================================================
// == Theme Swipers =============
// One place for every Swiper on the site. Templates only supply markup; any
// element matching a selector below is initialised automatically, so there are
// no inline <script> tags and no {% if %} guards around init code (no element
// on the page = nothing to init).
//
// Adding a swiper:   add an entry to SWIPERS.
// Variant on a page: add data-swiper-preset="other-preset-name" to the element.
// One-off tweaks:    add data-swiper-options='{"slidesPerView": 2}' to the
//                    element; it is deep-merged over the preset.
// Dynamic content:   call window.initThemeSwipers(rootElement) after injecting
//                    markup (already-initialised elements are skipped).

// -- helpers ------------------------------------------------------------------

const MOUSEWHEEL = { forceToAxis: true, hideOnClick: true };

// Next/prev selectors: `.{prefix}-next` / `.{prefix}-prev`
const nav = (prefix) => ({ nextEl: `.${prefix}-next`, prevEl: `.${prefix}-prev` });

// 1 slide on mobile, 2 from md, 3 from xl
const cardBreakpoints = (gap) => ({
  768: { slidesPerView: 2, spaceBetween: gap },
  1200: { slidesPerView: 3, spaceBetween: gap },
});

const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

const deepMerge = (target, source) => {
  Object.keys(source).forEach((key) => {
    target[key] = isPlainObject(source[key]) && isPlainObject(target[key])
      ? deepMerge({ ...target[key] }, source[key])
      : source[key];
  });
  return target;
};

// -- presets ------------------------------------------------------------------
// options: object, or (el, slideCount) => object
// minSlides: skip init unless the element has at least this many slides

// Card carousels (specs, plans, locations, featured). Arrows and bullets are found inside the
// swiper's parent wrapper, so several carousels can share a page without sharing controls.
// Gap between cards: data-swiper-gap="20" (default 24).
const cards = (extra = {}) => ({
  options: (el) => {
    const wrap = el.parentElement;
    const gap = Number(el.dataset.swiperGap) || 24;
    return {
      ...extra,
      breakpoints: cardBreakpoints(gap),
      navigation: {
        nextEl: wrap.querySelector('.swiper-button-next'),
        prevEl: wrap.querySelector('.swiper-button-prev'),
      },
      pagination: { el: wrap.querySelector('.swiper-pagination'), type: 'bullets', clickable: true },
    };
  },
});

const PRESETS = {
  // Detail page hero (plans, specs, MLS, locations, masterplans)
  hero: {
    minSlides: 2,
    options: { navigation: nav('hero-button') },
  },

  // Card carousels (featured, incentive specs/locations)
  cards: cards(),
  'cards-centered': cards({ centerInsufficientSlides: true }),

  // Gallery (inc_gallery, gallery page)
  gallery: {
    minSlides: 1,
    options: {
      slidesPerView: 1,
      spaceBetween: 20,
      loop: false,
      mousewheel: MOUSEWHEEL,
      breakpoints: {
        576: { slidesPerView: 2, spaceBetween: 20 },
        768: { slidesPerView: 2, spaceBetween: 20 },
        992: { slidesPerView: 3, spaceBetween: 20 },
        1200: { slidesPerView: 3, spaceBetween: 20 },
      },
      navigation: nav('gallery-button'),
    },
  },

  testimonials: {
    minSlides: 2,
    options: {
      slidesPerView: 1,
      spaceBetween: 25,
      loop: false,
      autoHeight: true,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      navigation: nav('testimonials-button'),
    },
  },

  // Home page hero slider
  'home-hero': {
    minSlides: 2,
    options: {
      autoplay: { delay: 5000, disableOnInteraction: false },
      effect: 'fade',
      crossFade: true,
      parallax: true,
      loop: true,
      speed: 1000,
      slidesPerView: 1,
      spaceBetween: 0,
      mousewheel: MOUSEWHEEL,
      pagination: { el: '.hero-pagination', clickable: true },
    },
  },

  // Site alert banner: loops only when there is more than one message
  'alert-banner': {
    options: (el, slides) => ({
      loop: slides > 1,
      speed: 600,
      slidesPerView: 1,
      spaceBetween: 0,
      autoHeight: false,
      centeredSlides: false,
      watchOverflow: true,
      autoplay: { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true },
      navigation: nav('swiper-alert-banner-button'),
      a11y: { enabled: true, prevSlideMessage: 'Previous banner', nextSlideMessage: 'Next banner' },
      keyboard: { enabled: true, onlyInViewport: true },
    }),
  },

  // Content blocks: markup is id="img-block-{n}" with matching
  // .content-block-next-{n} / -prev-{n} / -pagination-{n} controls
  'content-block': {
    options: (el) => {
      const n = el.id.replace('img-block-', '');
      return {
        slidesPerView: 1,
        spaceBetween: 20,
        loop: false,
        navigation: { nextEl: `.content-block-next-${n}`, prevEl: `.content-block-prev-${n}` },
        pagination: { el: `.content-block-pagination-${n}`, type: 'bullets', clickable: true },
      };
    },
  },
};

// -- selector -> preset -------------------------------------------------------

const SWIPERS = [
  { selector: '.hero-swiper', preset: 'hero' },
  { selector: '.cards-swiper', preset: 'cards' },
  { selector: '.gallery-swiper', preset: 'gallery' },
  { selector: '.testimonials-swiper', preset: 'testimonials' },
  { selector: '.hero-slider', preset: 'home-hero' },
  { selector: '.alert-banner-swiper', preset: 'alert-banner' },
  { selector: '.img-block-swiper', preset: 'content-block' },
];

// -- init ---------------------------------------------------------------------

const readOverrides = (el) => {
  if (!el.dataset.swiperOptions) return {};
  try {
    return JSON.parse(el.dataset.swiperOptions);
  } catch (e) {
    console.warn('Invalid data-swiper-options JSON on', el, e);
    return {};
  }
};

const initSwiper = (el, defaultPreset) => {
  if (el.swiper) return;

  const name = el.dataset.swiperPreset || defaultPreset;
  const preset = PRESETS[name];
  if (!preset) {
    console.warn(`Unknown swiper preset "${name}" on`, el);
    return;
  }

  const slides = el.querySelectorAll('.swiper-slide').length;
  if (slides < (preset.minSlides || 0)) return;

  const base = typeof preset.options === 'function' ? preset.options(el, slides) : preset.options;
  new Swiper(el, deepMerge(deepMerge({}, base), readOverrides(el)));
};

const initThemeSwipers = (root = document) => {
  SWIPERS.forEach(({ selector, preset }) => {
    root.querySelectorAll(selector).forEach((el) => initSwiper(el, preset));
  });
};

window.initThemeSwipers = initThemeSwipers;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initThemeSwipers());
} else {
  initThemeSwipers();
}
